import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors } from 'context';
import { usePromoCodes } from 'core';

/**
 * «Мои бонусы» — промокоды студента (ТЗ 0039, шаг 6).
 *
 * 🔴 Текст обязан говорить прямо: код НЕ применяет скидку сам, его называют
 * менеджеру при оформлении. Иначе человек ждёт автоматической скидки, не
 * получает её и считает систему сломанной — а никакой поломки нет, так решено.
 *
 * Копирование — синхронно внутри `onClick`: `navigator.clipboard` вне
 * пользовательского жеста молча отклоняется, и студент вставляет чужой текст
 * из буфера (шрам `pages/Diplomas`).
 */
interface IGrowthPromoCodes {
  /** Куда идти с кодом. Без этой ссылки карточка — тупик. */
  siteUrl: string;
}

export const GrowthPromoCodes = memo(({ siteUrl }: IGrowthPromoCodes) => {
  const { t, i18n } = useTranslation();
  const { codes } = usePromoCodes();
  const [copied, setCopied] = useState<string | null>(null);

  if (!codes.length) return null;

  return (
    <section>
      <SectionTitle>{t('growth.promo.title')}</SectionTitle>
      <Hint>{t('growth.promo.howItWorks')}</Hint>

      {siteUrl ? (
        <CoursesLink href={siteUrl} target="_blank" rel="noopener noreferrer">
          {t('growth.promo.browseCourses')}
        </CoursesLink>
      ) : null}

      <Grid>
        {codes.map((row) => (
          <Card key={row.id} used={row.status === 'redeemed'}>
            <CampaignTitle>{row.campaignTitle}</CampaignTitle>
            <Discount>
              {row.discountKind === 'percent'
                ? t('growth.promo.percent', { n: row.discountValue })
                : t('growth.promo.amount', {
                    n: row.discountValue,
                    currency: row.currency,
                  })}
            </Discount>

            <CodeRow>
              <Code>{row.code}</Code>
              <CopyButton
                type="button"
                onClick={() => {
                  // Синхронно, до любого await — иначе жест «потерян».
                  navigator.clipboard?.writeText(row.code);
                  setCopied(row.id);
                }}>
                {copied === row.id ? t('growth.promo.copied') : t('growth.promo.copy')}
              </CopyButton>
            </CodeRow>

            {row.campaignDescription ? <Description>{row.campaignDescription}</Description> : null}

            <Footer>
              {row.status === 'redeemed'
                ? t('growth.promo.used')
                : row.expiresAt
                ? t('growth.promo.until', {
                    date: new Date(row.expiresAt).toLocaleDateString(i18n.language),
                  })
                : t('growth.promo.noExpiry')}
            </Footer>
          </Card>
        ))}
      </Grid>
    </section>
  );
});

const SectionTitle = styled.h2`
  margin: 0 0 6px;
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const Hint = styled.p`
  margin: 0 0 12px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const CoursesLink = styled.a`
  display: inline-block;
  margin-bottom: 12px;
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${Colors.black};
  text-decoration: underline;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ used: boolean }>`
  padding: 16px 18px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px dashed ${({ used }) => (used ? Colors.gray : Colors.main)};
  opacity: ${({ used }) => (used ? 0.7 : 1)};
`;

const CampaignTitle = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const Discount = styled.span`
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${Colors.marker_green};
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const CodeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Code = styled.span`
  padding: 4px 10px;
  border-radius: 6px;
  background: ${Colors.bg_gray};
  letter-spacing: 0.08em;
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const CopyButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${Colors.black};
  text-decoration: underline;
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Description = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const Footer = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;
